import React, { Component, ReactNode } from "react";

// Define a type for the notification function
type NotifyFunction = (notification: {
  status: string;
  txHash: string;
  errorReason?: string;
  duration?: number;
  chainType?: string;
}) => void;

interface ErrorBoundaryState {
  hasError: boolean;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  setNotify?: NotifyFunction; // Optional prop for notification
}

class WalletErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  timeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.warn("Error caught by WalletErrorBoundary:", error, errorInfo);

    // Check if this is a wallet-related error
    const isWalletError =
      error?.message?.includes("could not coalesce error") ||
      error?.message?.includes("missing trie node") ||
      error?.message?.includes("Internal JSON-RPC error") ||
      error?.message?.includes("Failed to connect") ||
      error?.message?.includes("UNKNOWN_ERROR") ||
      error?.message?.includes("network error") ||
      error?.message?.includes("connection failed");

    if (isWalletError) {
      console.warn("Wallet error caught by ErrorBoundary:", error);

      // Use notification if available, otherwise dispatch event
      if (this.props.setNotify) {
        this.props.setNotify({
          status: "error",
          txHash: `undefined`,
          errorReason:
            "Wallet connection error. Wallet disconnected. Please reconnect your wallet",
          duration: 10000,
          chainType: "BNB",
        });
      } else {
        // Fallback to custom event
        window.dispatchEvent(
          new CustomEvent("wallet-disconnect-required", {
            detail: { error, reason: "Uncaught wallet error" },
          })
        );
      }
    } else {
      // For non-wallet errors, still show notification if available
      if (this.props.setNotify) {
        this.props.setNotify({
          status: "error",
          txHash: `undefined`,
          errorReason: `Application Error: ${error.message}`,
          duration: 8000,
          chainType: "BNB",
        });
      }
    }
  }

  componentDidUpdate(_: ErrorBoundaryProps, prevState: ErrorBoundaryState) {
    // When error appears, schedule reset after 1 second
    if (this.state.hasError && !prevState.hasError) {
      if (this.timeoutId) clearTimeout(this.timeoutId);
      this.timeoutId = setTimeout(() => {
        this.setState({ hasError: false });
      }, 1000); // <-- 1 second delay
    }
  }

  componentWillUnmount() {
    if (this.timeoutId) clearTimeout(this.timeoutId);
  }

  render() {
    if (this.state.hasError) {
      return null; // Don't render anything, just reset
    }

    return this.props.children;
  }
}

export default WalletErrorBoundary;
